import _ from "lodash";

import { supabase } from "config/supabase";
import Logger from "utils/logger";
import CurrentSession from "data/userSession";
import { monitored } from "utils/queryMonitor";

// Base for models backed by Supabase tables. Mirrors the public API of the
// Firestore base (fetch/save/delete/changesMade/saveCheckpoint) so subclasses and
// their callers barely change. Subclasses provide:
//   static COLLECTION   -- table name
//   get json            -- camelCase snapshot, used only for change detection
//   toRow()             -- snake_case row to write (id + uid are added here)
//   static fromRow(row) -- build an instance from a table row

// Queries are memoized by an explicit key (per request cycle), like the Firestore
// layer. Reset on any write so the next read sees fresh data. `columns` is part of
// the key so the same key with different projections doesn't collide.
const cacheKey = (collection, _buildFn, key, columns = "*") =>
  `${collection} ${key} ${columns}`;

const QueryCache = _.memoize((collection, buildFn, key, columns = "*") => {
  const promise = monitored(`SB ${collection} ${key}`, async () => {
    let q = supabase.from(collection).select(columns);
    q = buildFn(q);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  });
  // memoize caches the promise itself; if it rejects (hung token timeout,
  // transient network on resume) evict the key so the next call retries instead
  // of forever awaiting the dead promise -- otherwise only a restart recovers.
  promise.catch(() =>
    QueryCache.cache.delete(cacheKey(collection, buildFn, key, columns))
  );
  return promise;
}, cacheKey);

// Same memoize-by-key + evict-on-reject contract as QueryCache, for RPCs (e.g. a
// server-side distinct) where select("*") doesn't fit. Cleared on any write below.
const rpcCacheKey = (fn, _args, key) => `${fn} ${key}`;

const RpcCache = _.memoize((fn, args, key) => {
  const promise = monitored(`SB rpc ${fn} ${key}`, async () => {
    const { data, error } = await supabase.rpc(fn, args);
    if (error) throw error;
    return data || [];
  });
  promise.catch(() => RpcCache.cache.delete(rpcCacheKey(fn, args, key)));
  return promise;
}, rpcCacheKey);

// Single-row fetch by id, memoized per (table, id) like QueryCache. Without this a
// screen that builds several instances of the same row (e.g. the profile page
// mounts 3-4 charts, each doing `new User(uid).load()`) re-hit the expensive
// users_public view once per chart. Same evict-on-reject + reset-on-write contract.
const fetchCacheKey = (collection, id) => `${collection} ${id}`;
const FetchCache = _.memoize((collection, id) => {
  const promise = monitored(`SB ${collection} fetch ${id}`, async () => {
    const { data, error } = await supabase
      .from(collection)
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    return { id: data?.id ?? null, data };
  });
  promise.catch(() => FetchCache.cache.delete(fetchCacheKey(collection, id)));
  return promise;
}, fetchCacheKey);

export default class SupabaseDocument {
  constructor() {
    this.migrated = false;
  }

  get changesMade() {
    return !_.isEqual(this.json, this.json_original);
  }

  // Throw a precise, screenshot-debuggable error when the current session isn't
  // allowed to write this row -- naming the table and exactly which guard tripped,
  // instead of one vague "insufficient permissions" for all four cases.
  _assertCanSave() {
    const table = this.constructor.COLLECTION || "this record";
    const uid = CurrentSession.authenticatedUser?.uid;
    const owner = this.uid || this.id; // row owner (User: uid===id; Verification: id)
    if (!CurrentSession.isSignedIn)
      throw new Error(`Can't save ${table}: you're not signed in.`);
    if (!uid)
      throw new Error(
        `Can't save ${table}: your account hasn't finished loading. Try again in a moment.`
      );
    if (!CurrentSession.authenticationStrictlyMatches(uid))
      throw new Error(
        `Can't save ${table}: your session doesn't match the signed-in account. Sign out and back in.`
      );
    if (owner && owner !== uid)
      throw new Error(
        `Can't save ${table}: this record belongs to a different account.`
      );
  }

  // Wrap a Supabase/PostgREST write error with the table, operation, and its
  // code/message/hint so a failure is diagnosable from a user's screenshot (e.g.
  // "[42501] ... row-level security policy" tells us which table was banned and why).
  // The original error is kept on `.cause` for logging.
  _writeError(table, error, op = "save") {
    const code = error?.code ? ` [${error.code}]` : "";
    const detail = error?.message || String(error);
    const hint = error?.hint ? ` — ${error.hint}` : "";
    const wrapped = new Error(
      `Failed to ${op} ${table}${code}: ${detail}${hint}`
    );
    wrapped.cause = error;
    return wrapped;
  }

  // Returns { id, data } where data is the raw row (or null), matching the old
  // Firestore fetch() shape used by fromID().
  async fetch() {
    return FetchCache(this.constructor.COLLECTION, this.id);
  }

  // buildFn receives a supabase query builder and returns it with filters applied.
  // columns defaults to "*"; pass a projection (e.g. "id,username,location") to
  // fetch only what's needed when full rows are wasteful (map pins, lists).
  static async query(collection, buildFn, key, columns = "*") {
    const rows = await QueryCache(collection, buildFn, key, columns);
    Logger.debug(`[SB-DB] ${collection} ${key}: ${rows.length}`);
    return rows;
  }

  // Memoized RPC call (same per-cycle cache + reset-on-write as query()).
  static async rpc(fn, args, key) {
    const rows = await RpcCache(fn, args, key);
    Logger.debug(`[SB-DB] rpc ${fn} ${key}: ${rows.length}`);
    return rows;
  }

  static resetCache() {
    QueryCache.cache.clear();
    RpcCache.cache.clear();
    FetchCache.cache.clear();
  }

  async delete() {
    if (!this.id) return;
    const { error } = await supabase
      .from(this.constructor.COLLECTION)
      .delete()
      .eq("id", this.id);
    if (error)
      throw this._writeError(this.constructor.COLLECTION, error, "delete");
    // Invalidate AFTER the write commits. Clearing before (while the delete is
    // in flight) leaves a window where a concurrent read repopulates the cache
    // with pre-commit rows, so the next page shows stale data.
    SupabaseDocument.resetCache();
  }

  async save() {
    // Skip no-op saves on existing rows (new rows have no id yet).
    if (!this.changesMade && this.id) {
      return;
    }
    this._assertCanSave();

    if (!this.id) {
      // ponytail: crypto.randomUUID needs a secure context (fine in Capacitor +
      // https); swap in a uuid lib if a legacy WebView ever chokes.
      this.id = crypto.randomUUID();
      this.uid = CurrentSession.authenticatedUser.uid;
    }

    const row = { ...this.toRow(), id: this.id, uid: this.uid };
    // Snapshot exactly what we're persisting, BEFORE the await. If the object is
    // mutated mid-save (e.g. the user hits Start while a field-blur save is in
    // flight), the checkpoint must reflect the row we actually wrote -- not the
    // newer state -- otherwise the follow-up save sees no diff and silently drops
    // the newer change (Start getting ignored).
    const written = _.cloneDeep(this.json);
    Logger.debug(`[SB-DB] Saving ${this.constructor.COLLECTION}/${this.id}`);
    const { error } = await supabase
      .from(this.constructor.COLLECTION)
      .upsert(row);
    if (error)
      throw this._writeError(this.constructor.COLLECTION, error, "upsert");

    this.migrated = false;
    this.json_original = written;
    // Invalidate AFTER the write commits. Clearing before (while the upsert is
    // in flight) leaves a window where a concurrent read (e.g. the tug page's
    // "today" total) repopulates the cache with pre-commit rows, so the next
    // page shows stale data.
    SupabaseDocument.resetCache();
  }

  saveCheckpoint() {
    this.json_original = _.cloneDeep(this.json);
  }
}
